import { Injectable } from '@angular/core';
import { countries } from '../countries';
import { PhoneNumberUtil } from 'google-libphonenumber';
import { environment } from '@env/environment';
import { AbstractControl, ValidatorFn } from '@angular/forms';
import { Country } from '../models/country.model';

export interface Phone {
  dial_code: string;
  phone: string;
}

export interface PhoneInputConfiguration {
  code: string;
  phoneNumber: string;
}

@Injectable({
  providedIn: 'root',
})
export class PhoneService {
  countryCode = '+' + environment.countrySettings.countryCode;
  configuration: PhoneInputConfiguration | undefined;

  constructor() {}

  getCountries(): Country[] {
    countries.sort((a: Country, b: Country) => {
      return a.dial_code > b.dial_code ? 1 : -1;
    });
    return countries;
  }

  getPhoneObject(phone: string): Phone {
    // if the phone is null return.
    if (!phone) return null;
    const codes = this.getCodesTable();
    return this.getFormattedPhone(phone, codes);
  }

  isValidNumber(phone: string, regionCode: string = undefined): boolean {
    try {
      const phoneNumberUtil = PhoneNumberUtil.getInstance();
      const phoneNumber = phoneNumberUtil.parseAndKeepRawInput(
        phone,
        regionCode,
      );
      return (
        phoneNumberUtil.isValidNumber(phoneNumber) ||
        phoneNumberUtil.isPossibleNumber(phoneNumber)
      );
    } catch (ex) {
      return false;
    }
  }

  private getFormattedPhone(
    phone: string,
    codesTable: { [key: string]: Country },
  ) {
    const p = `${phone[0] === '+' ? '' : '+'}${phone}`;
    let i = 1;
    const max = 5;
    while (i <= max) {
      if (codesTable[p.substr(0, i)]) {
        const code = p.substr(0, i);
        return { dial_code: code, phone: p.substr(code.length) };
      }
      i++;
    }

    // default code is the country from env if no code found
    // check if the phone number starts with 0, that means the user entered the
    // local phone number without the country code, so we automatically add the country code to it
    // remove the 0 if the number start with 0
    const num = phone.startsWith('0') ? phone.substr(1) : phone;
    return { dial_code: this.countryCode, phone: num };
  }

  private getCodesTable(): { [key: string]: Country } {
    const codes: { [key: string]: Country } = {};
    this.getCountries().forEach((country: Country) => {
      codes[country.dial_code] = country;
    });
    return codes;
  }

  filterCountryCode(code: string) {
    const countryCode = code.toLowerCase();
    return countries.filter((option: Country) =>
      option.dial_code.toLowerCase().includes(countryCode),
    );
  }

  splitCountryCodeFromPhone(phone: string) {
    const phoneUtil = PhoneNumberUtil.getInstance();
    try {
      const numberProto = phoneUtil.parse(phone, '');
      return {
        countryCode: '+' + numberProto.getCountryCode(),
        nationalNumber: numberProto.getNationalNumber(),
      };
    } catch (ex) {}
  }

  PhoneNumberValidator(regionCode: string = undefined): ValidatorFn {
    return (control: AbstractControl) => {
      let validNumber = false;
      try {
        const code = regionCode;
        const val = `${control.value}`;
        if (val.startsWith('0')) {
          return { wrongNumber: { value: control.value } };
        }
        // contains invalid characters
        if (
          val.indexOf(' ') !== -1 ||
          val.indexOf('-') !== -1 ||
          val.indexOf('(') !== -1
        ) {
          return { wrongNumber: { value: control.value } };
        }
        validNumber = this.isValidNumber(`${code}${val}`);
      } catch (e) {}
      return validNumber ? null : { wrongNumber: { value: control.value } };
    };
  }
}
